
import React, { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import HighlightedText from "components/Shared/HighlightedText";
import { BaseTabs } from "components/Shared/BaseTabs";
import TableFilter from "components/Shared/AppTable/TableFilter";
import BaseSelect from "components/Shared/Base/BaseSelect";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import { useLazyListContractorQuery } from "store/services/contractorApi";
import { useTablePagination } from "hooks";
import { useAuth } from "hooks/useAuth";
import { Button } from "@mui/material";
import { Add } from "@mui/icons-material";

const ContractorDriverListScreen = () => {
  const { loggedInUser } = useAuth();
  const contractorUser = loggedInUser?.typeRef ;
  
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } = useTablePagination();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "Active";
  const keyword = searchParams.get("search") || "";
  const [sortOrder, setSortOrder] = useState("createdAt:-1");
  const [searchTerm, setSearchTerm] = useState(keyword);

  const breadcrumbs = [{ title: "Drivers" }];
  const driverListTabs = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
  ];

  const [fetchDrivers, { data: driverRes, isLoading, isFetching, error }] = useLazyListContractorQuery();

  useEffect(() => {
   
      fetchDrivers({
        Active: activeTab === "Active",
        page: currentPage,
        limit: perPage,
        ...(keyword && { keyword }),
        contractorUser,
      });
  }, [activeTab, currentPage, fetchDrivers, keyword, perPage, sortOrder, contractorUser]);

  const groupedDriverData = useMemo(() => {
    if (!driverRes?.data) return [];
    return driverRes.data.flatMap((contractorDriver) => {
      const vehicles = contractorDriver.vehicle || [];
      const drivers = contractorDriver.driverId || [];
  
      return drivers.map((driver) => {
        const assignedVehicles = driver.assigned_vehicle_ids || [];
        const vehicleIds = assignedVehicles
          .map((vid) => {
            const vehicle = vehicles.find((v) => v._id === vid);
            return vehicle?.vehicle_id;
          })
          .filter(Boolean);
  
        return {
          _id: driver._id,
          name: driver.name,
          driverId: driver.driverId,
          email: driver.email,
          phone: driver.phone,
          vehicleIds,
          contractorName: contractorDriver.name,
        };
      });
    });
  }, [driverRes?.data]);
  

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setSearchParams({ tab: activeTab, search: value, sortBy: sortOrder });
  };

  const handleSortChange = (sortField) => {
    setSortOrder(sortField);
  };

  const columns = [
    {
      name: "Contractor Name",
      selector: (row) => row.contractorName,
      sortable: true,
      cell: (row) => row?.contractorName || "N/A",
    },
    
    {
      name: "Driver Name",
      selector: (row) => row.name,
      sortable: true,
      cell: (row) => row?.name || "N/A",
    },
    {
      name: "Driver ID",
      selector: (row) => row.driverId,
      sortable: true,
      cell: (row) => (
        <Link
          title={row?.driverId}
          to={`/app/contractor/driver/${row?._id}/detail`}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.driverId ? <HighlightedText text={row?.driverId} highlight={keyword} /> : "N/A"}
        </Link>
      ),
    },
    {
      name: "Vehicle ID",
      selector: (row) => row.vehicleIds?.join(", ") || "N/A",
      sortable: true,
      cell: (row) => row.vehicleIds?.join(", ") || "N/A",
    },
    {
      name: "Email",
      selector: (row) => row.email,
      sortable: true,
      cell: (row) => row?.email || "N/A",
    },
    {
      name: "Phone",
      selector: (row) => row.phone,
      sortable: true,
      cell: (row) => row?.phone || "N/A",
    },
    
  ];
  

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
      <BreadcrumbLayout.Error error={error} />
      <BreadcrumbLayout.Paper>
        <BaseTabs tabs={driverListTabs} defaultValue={activeTab} />

        <TableFilter
          searchLabel="Search Drivers"
          value={searchTerm}
          onChange={handleSearchChange}
          disabled={isLoading || isFetching}
        >
          <BaseSelect
            fullWidth
            label="Sort By"
            value={sortOrder}
            onChange={(e) => handleSortChange(e.target.value)}
            options={[
              { label: "CreatedAt (Desc)", value: "createdAt:-1" },
              { label: "CreatedAt (Asc)", value: "createdAt:1" },
              { label: "UpdatedAt (Desc)", value: "updatedAt:-1" },
              { label: "UpdatedAt (Asc)", value: "updatedAt:1" },
            ]}
            getOptionLabel={(opt) => opt.label}
            getOptionValue={(opt) => opt.value}
          />
        </TableFilter>

        <AppTable
          columns={columns}
          data={groupedDriverData}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={driverRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
          onSort={handleSortChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default ContractorDriverListScreen;

const PageActions = () => {
  return (
    <Link to="/app/contractor/driver/create">
      <Button startIcon={<Add />} variant="contained">
        New Driver
      </Button>
    </Link>
  );
};
